import React, { useState, useEffect, useRef, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../contexts/ThemeContext";
import {
  PlayIcon,
  DocumentPlusIcon,
  FolderOpenIcon,
  DocumentArrowDownIcon,
  XMarkIcon,
  CloudArrowUpIcon,
  CodeBracketIcon,
} from "@heroicons/react/24/outline";
import Editor from "@monaco-editor/react";

// Import Prism core
import "prismjs";
// Import specific languages
import "prismjs/components/prism-clike";
import "prismjs/components/prism-javascript";
import "prismjs/components/prism-c";
import "prismjs/components/prism-cpp";
import "prismjs/components/prism-java";
import "prismjs/components/prism-python";
import "prismjs/components/prism-csharp";
import "prismjs/components/prism-go";
import "prismjs/components/prism-ruby";
import "prismjs/components/prism-rust";
import "prismjs/components/prism-php";
// Import CSS themes
import "prismjs/themes/prism.css";
import "prismjs/themes/prism-dark.css";
import "prismjs/themes/prism-okaidia.css";
import "prismjs/themes/prism-tomorrow.css";
import "prismjs/themes/prism-solarizedlight.css";
import "prismjs/themes/prism-twilight.css";

const IDETab = ({ topic }) => {
  const { theme } = useContext(ThemeContext);
  const [judgeLanguages, setJudgeLanguages] = useState([]);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(
    theme === "dark" ? "vs-dark" : "light"
  );

  // Multiple files state with localStorage persistence
  const [files, setFiles] = useState(() => {
    const savedFiles = localStorage.getItem("ide_files");
    if (savedFiles) {
      try {
        return JSON.parse(savedFiles);
      } catch (e) {
        console.error("Failed to parse saved files", e);
      }
    }
    // Default state if nothing in localStorage
    return [
      {
        id: "file1",
        name: "main.cpp",
        content: `#include <bits/stdc++.h>

using namespace std;

int main() {
    // Your code here
    cout << "Hello, World!" << endl;
    
    // Input example
    // int n;
    // cin >> n;
    
    // Vector example
    // vector<int> numbers(n);
    // for(int i = 0; i < n; i++) {
    //     cin >> numbers[i];
    // }
    
    return 0;
}`,
        language: "cpp",
        languageId: null,
      },
    ];
  });

  const [activeFileId, setActiveFileId] = useState(() => {
    const savedActiveFileId = localStorage.getItem("ide_active_file_id");
    return savedActiveFileId || "file1";
  });

  const [input, setInput] = useState(() => {
    const savedInput = localStorage.getItem("ide_input");
    return savedInput || "";
  });

  const [output, setOutput] = useState("");
  const [status, setStatus] = useState("Ready");
  const [isRunning, setIsRunning] = useState(false);
  const [activeTab, setActiveTab] = useState(() => {
    const savedActiveTab = localStorage.getItem("ide_active_tab");
    return savedActiveTab || "output";
  });
  const [editingFile, setEditingFile] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [serverSaveStatus, setServerSaveStatus] = useState("");
  const [showServerSaveMessage, setShowServerSaveMessage] = useState(false);

  const fileNameInputRef = useRef(null);
  const editorRef = useRef(null);
  const monacoRef = useRef(null);

  // Get active file
  const activeFile = files.find((file) => file.id === activeFileId) || files[0];

  // Judge0 API settings
  const api_url = "https://judge0-ce.p.rapidapi.com";
  const headers = {
    "Content-Type": "application/json",
    "x-rapidapi-host": "judge0-ce.p.rapidapi.com",
    "x-rapidapi-key": "fe9529418cmsh4f9b961b0fa1345p14c4dajsn1ac0e7358b20",
  };

  // Monaco editor themes
  const editorThemes = [
    { label: "Light", value: "light" },
    { label: "Dark", value: "vs-dark" },
    { label: "High Contrast", value: "hc-black" },
  ];

  // Backend URL for API calls
  const backendURL = process.env.REACT_APP_BACKEND_URL;

  // Function to handle editor mounting
  const handleEditorDidMount = (editor, monaco) => {
    editorRef.current = editor;
    monacoRef.current = monaco;

    // Set initial options
    editor.updateOptions({
      fontSize: 20,
      fontFamily: "Consolas, 'Courier New', monospace",
      lineNumbers: "on",
      minimap: { enabled: true },
      scrollBeyondLastLine: false,
      automaticLayout: true,
      // Add VS Code features
      formatOnPaste: true,
      formatOnType: true,
      autoIndent: "full",
      tabSize: 4,
      bracketPairColorization: {
        enabled: true,
      },
      renderWhitespace: "selection",
      folding: true,
      foldingHighlight: true,
      foldingStrategy: "auto",
      showFoldingControls: "always",
      wordWrap: "on",
      wordWrapColumn: 80,
      wordWrapMinified: true,
      wrappingIndent: "indent",
      matchBrackets: "always",
      cursorBlinking: "smooth",
    });

    // Register language formatting providers
    registerFormatters(monaco);

    // Register keyboard command for formatting
    editor.addAction({
      id: "format-document-custom",
      label: "Format Document",
      keybindings: [
        monaco.KeyMod.Shift | monaco.KeyMod.Alt | monaco.KeyCode.KeyF,
      ],
      run: formatCode,
    });
  };

  // Register formatters for different languages
  const registerFormatters = (monaco) => {
    // Languages that support formatting
    const languages = [
      "javascript",
      "typescript",
      "json",
      "css",
      "html",
      "cpp",
      "c",
      "csharp",
      "java",
      "python",
    ];

    languages.forEach((language) => {
      monaco.languages.registerDocumentFormattingEditProvider(language, {
        provideDocumentFormattingEdits: function (model) {
          // Basic indentation-based formatting
          return [
            {
              range: model.getFullModelRange(),
              text: prettifyCode(model.getValue(), language),
            },
          ];
        },
      });
    });
  };

  // Basic code prettify function
  const prettifyCode = (code, language) => {
    try {
      // Very basic formatting: normalize indentation
      let lines = code.split("\n");
      let indentLevel = 0;
      let formattedLines = [];

      const indentChar = "    "; // 4 spaces

      for (let i = 0; i < lines.length; i++) {
        let line = lines[i].trim();

        // Handle bracket indentation
        if (line.match(/^[})\]]/)) {
          indentLevel = Math.max(0, indentLevel - 1);
        }

        if (line.length > 0) {
          formattedLines.push(indentChar.repeat(indentLevel) + line);
        } else {
          formattedLines.push("");
        }

        // Increase indent for next line
        if (line.match(/[{([]$/)) {
          indentLevel++;
        }
      }

      return formattedLines.join("\n");
    } catch (e) {
      console.error("Error prettifying code:", e);
      return code; // Return unchanged if error
    }
  };

  // Add a function to format code with better error handling
  const formatCode = () => {
    try {
      if (editorRef.current) {
        // Try the built-in format action first
        const formatAction = editorRef.current.getAction(
          "editor.action.formatDocument"
        );

        if (formatAction) {
          formatAction
            .run()
            .then(() => {
              setStatus("Code formatted");
            })
            .catch((err) => {
              console.log("Falling back to custom formatter", err);
              // If built-in formatter fails, use our basic formatter
              if (monacoRef.current && editorRef.current) {
                const model = editorRef.current.getModel();
                if (model) {
                  const language = activeFile ? activeFile.language : "cpp";
                  const formatted = prettifyCode(model.getValue(), language);
                  model.setValue(formatted);
                  setStatus("Code formatted (basic formatting)");
                }
              }
            });
        } else {
          // Use our basic formatter if built-in isn't available
          if (monacoRef.current && editorRef.current) {
            const model = editorRef.current.getModel();
            if (model) {
              const language = activeFile ? activeFile.language : "cpp";
              const formatted = prettifyCode(model.getValue(), language);
              model.setValue(formatted);
              setStatus("Code formatted (basic formatting)");
            }
          }
        }
      }
    } catch (error) {
      console.error("Format error:", error);
      setStatus("Error formatting code: " + error.message);
    }
  };

  // Handle keyboard shortcuts
  const handleKeyDown = (e) => {
    // Ctrl+N: New file
    if (e.ctrlKey && e.key === "n") {
      e.preventDefault();
      newFile();
    }
    // Ctrl+O: Open file
    else if (e.ctrlKey && e.key === "o") {
      e.preventDefault();
      openFile();
    }
    // Ctrl+S: Save file
    else if (e.ctrlKey && e.key === "s") {
      e.preventDefault();
      saveFile();
    }
    // F5: Run code
    else if (e.key === "F5") {
      e.preventDefault();
      runCode();
    }
    // Ctrl+W: Close current tab
    else if (e.ctrlKey && e.key === "w") {
      e.preventDefault();
      closeFile(activeFileId);
    }
    // Shift+Alt+F: Format code
    else if (e.shiftKey && e.altKey && e.key === "f") {
      e.preventDefault();
      formatCode();
    }
  };

  useEffect(() => {
    // Setup keyboard shortcuts
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFileId]);

  // Load languages only once on component mount
  useEffect(() => {
    loadLanguages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Set selected language when active file changes
  useEffect(() => {
    const currentFile = files.find((file) => file.id === activeFileId);
    if (currentFile && currentFile.languageId) {
      const fileLanguage = judgeLanguages.find(
        (lang) => lang.id === currentFile.languageId
      );
      if (fileLanguage) {
        setSelectedLanguage(fileLanguage);
      }
    }
  }, [activeFileId, files, judgeLanguages]);

  // Update theme when app theme changes
  useEffect(() => {
    setSelectedTheme(theme === "dark" ? "vs-dark" : "light");
  }, [theme]);

  // Save files to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("ide_files", JSON.stringify(files));
  }, [files]);

  // Save activeFileId to localStorage
  useEffect(() => {
    localStorage.setItem("ide_active_file_id", activeFileId);
  }, [activeFileId]);

  // Save input to localStorage
  useEffect(() => {
    localStorage.setItem("ide_input", input);
  }, [input]);

  // Save activeTab to localStorage
  useEffect(() => {
    localStorage.setItem("ide_active_tab", activeTab);
  }, [activeTab]);

  // Save selected theme to localStorage
  useEffect(() => {
    localStorage.setItem("ide_theme", selectedTheme);
  }, [selectedTheme]);

  // Get Monaco language ID based on selected language
  const getMonacoLanguage = () => {
    if (!selectedLanguage) return "plaintext";

    const name = selectedLanguage.name.toLowerCase();

    if (name.includes("c++")) return "cpp";
    if (name.includes("c ")) return "c";
    if (name.includes("java ")) return "java";
    if (name.includes("python")) return "python";
    if (name.includes("javascript")) return "javascript";
    if (name.includes("c#")) return "csharp";
    if (name.includes("go")) return "go";
    if (name.includes("ruby")) return "ruby";
    if (name.includes("rust")) return "rust";
    if (name.includes("php")) return "php";

    return "plaintext";
  };

  // Get file language from file name
  const getFileLanguage = (fileName) => {
    // Check if the filename has an extension
    if (!fileName.includes(".")) {
      return "cpp"; // Default to C++ when no extension is present
    }

    const ext = fileName.split(".").pop().toLowerCase();

    const extensionMap = {
      py: "python",
      c: "c",
      cpp: "cpp",
      h: "cpp",
      hpp: "cpp",
      java: "java",
      js: "javascript",
      jsx: "javascript",
      ts: "typescript",
      tsx: "typescript",
      rb: "ruby",
      cs: "csharp",
      go: "go",
      rs: "rust",
      php: "php",
      html: "html",
      css: "css",
      json: "json",
      md: "markdown",
      txt: "plaintext",
    };

    // If extension exists in map, use it, otherwise default to cpp instead of plaintext
    return extensionMap[ext] || "cpp";
  };

  // Load languages from Judge0 API
  const loadLanguages = async () => {
    try {
      setStatus("Loading languages...");
      const response = await axios.get(`${api_url}/languages`, { headers });

      if (response.status === 200) {
        // Sort languages by name
        const sortedLanguages = response.data.sort((a, b) =>
          a.name.localeCompare(b.name)
        );

        setJudgeLanguages(sortedLanguages);

        // Get saved language ID from localStorage
        const savedLanguageId = localStorage.getItem(
          "ide_selected_language_id"
        );

        if (savedLanguageId) {
          const savedLanguage = sortedLanguages.find(
            (lang) => lang.id === parseInt(savedLanguageId)
          );
          if (savedLanguage) {
            setSelectedLanguage(savedLanguage);
            setStatus("Languages loaded successfully");
            return;
          }
        }

        // Set default language to C++
        const defaultLanguage = sortedLanguages.find((lang) =>
          lang.name.includes("C++ (GCC 14.1.0)")
        );

        if (defaultLanguage) {
          setSelectedLanguage(defaultLanguage);
          localStorage.setItem("ide_selected_language_id", defaultLanguage.id);
        } else if (sortedLanguages.length > 0) {
          setSelectedLanguage(sortedLanguages[0]);
          localStorage.setItem(
            "ide_selected_language_id",
            sortedLanguages[0].id
          );
        }

        setStatus("Languages loaded successfully");
      }
    } catch (error) {
      console.error("Error loading languages:", error);
      setStatus("Error loading languages");
    }
  };

  // Handle language change
  const handleLanguageChange = (e) => {
    const langId = parseInt(e.target.value);
    const language = judgeLanguages.find((lang) => lang.id === langId);
    setSelectedLanguage(language);

    // Store the selected language ID in localStorage
    localStorage.setItem("ide_selected_language_id", langId);

    // Update the file's languageId and monaco language
    updateFileLanguage(activeFileId, getMonacoLanguage(), langId);
  };

  // Update a file's language
  const updateFileLanguage = (fileId, monacoLanguage, languageId) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === fileId
          ? {
              ...file,
              language: monacoLanguage,
              languageId: languageId,
            }
          : file
      )
    );
  };

  // Handle theme change
  const handleThemeChange = (e) => {
    setSelectedTheme(e.target.value);
  };

  // Handle input change
  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  // Handle code change
  const handleCodeChange = (value) => {
    updateFileContent(activeFileId, value);
  };

  // Update file content
  const updateFileContent = (fileId, content) => {
    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === fileId ? { ...file, content } : file
      )
    );
  };

  // Create a new file
  const newFile = () => {
    const fileId = `file${Date.now()}`;
    const defaultExt = getDefaultExtension();
    const fileName = `untitled.${defaultExt}`;

    const newFile = {
      id: fileId,
      name: fileName,
      content: "",
      language: getFileLanguage(fileName),
      languageId: selectedLanguage?.id || null,
    };

    setFiles((prevFiles) => [...prevFiles, newFile]);
    setActiveFileId(fileId);
    setStatus(`Created new file: ${fileName}`);
  };

  // Open a file
  const openFile = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept =
      ".py,.c,.cpp,.h,.hpp,.java,.js,.jsx,.ts,.tsx,.rb,.cs,.go,.rs,.php,.html,.css,.json,.md,.txt";

    input.onchange = (e) => {
      const file = e.target.files[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (event) => {
        const fileId = `file${Date.now()}`;
        const fileContent = event.target.result;
        const monacoLanguage = getFileLanguage(file.name);

        // Find the corresponding Judge0 language
        const ext = file.name.split(".").pop().toLowerCase();
        const langName = getLanguageNameFromExtension(ext);
        let languageId = null;

        if (langName) {
          const matchingLang = judgeLanguages.find((lang) =>
            lang.name.includes(langName)
          );
          if (matchingLang) {
            languageId = matchingLang.id;
          }
        }

        const newFile = {
          id: fileId,
          name: file.name,
          content: fileContent,
          language: monacoLanguage,
          languageId: languageId || selectedLanguage?.id,
        };

        setFiles((prevFiles) => [...prevFiles, newFile]);
        setActiveFileId(fileId);

        setStatus(`Opened: ${file.name}`);
      };

      reader.readAsText(file);
    };

    input.click();
  };

  // Get language name from file extension
  const getLanguageNameFromExtension = (extension) => {
    // Default to C++ if no extension
    if (!extension) {
      return "C++";
    }

    const extensionMap = {
      py: "Python",
      c: "C",
      cpp: "C++",
      h: "C++",
      hpp: "C++",
      java: "Java",
      js: "JavaScript",
      rb: "Ruby",
      cs: "C#",
      go: "Go",
      rs: "Rust",
      php: "PHP",
    };

    return extensionMap[extension] || "C++";
  };

  // Close a file
  const closeFile = (fileId) => {
    // Don't close if it's the last file
    if (files.length <= 1) {
      setStatus("Cannot close the last tab");
      return;
    }

    const fileToClose = files.find((file) => file.id === fileId);

    if (fileToClose && fileToClose.content.trim() !== "") {
      if (
        !window.confirm(
          `Do you want to save changes to ${fileToClose.name} before closing?`
        )
      ) {
        // User chose not to save, just close
        removeFile(fileId);
        return;
      }
      // User chose to save before closing
      saveFile(fileId);
    }

    removeFile(fileId);
  };

  // Remove a file from the files array
  const removeFile = (fileId) => {
    setFiles((prevFiles) => prevFiles.filter((file) => file.id !== fileId));

    // If we're closing the active file, activate another one
    if (fileId === activeFileId) {
      const remainingFiles = files.filter((file) => file.id !== fileId);
      if (remainingFiles.length > 0) {
        setActiveFileId(remainingFiles[0].id);
      }
    }
  };

  // Save file
  const saveFile = (fileId = activeFileId) => {
    const fileToSave = files.find((file) => file.id === fileId);

    if (!fileToSave || !fileToSave.content.trim()) {
      setStatus("Nothing to save");
      return;
    }

    const blob = new Blob([fileToSave.content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileToSave.name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    setStatus(`Saved: ${fileToSave.name}`);
  };

  // Rename file
  const renameFile = (fileId, newName) => {
    // Get the language for the new filename, ensuring CPP for no extension
    const newLanguage = !newName.includes(".")
      ? "cpp"
      : getFileLanguage(newName);

    setFiles((prevFiles) =>
      prevFiles.map((file) =>
        file.id === fileId
          ? {
              ...file,
              name: newName,
              language: newLanguage,
            }
          : file
      )
    );
  };

  // Get default file extension based on selected language
  const getDefaultExtension = () => {
    if (!selectedLanguage) return "txt";

    const name = selectedLanguage.name.toLowerCase();
    if (name.includes("python")) return "py";
    if (name.includes("c++")) return "cpp";
    if (name.includes("c ")) return "c";
    if (name.includes("java")) return "java";
    if (name.includes("javascript")) return "js";
    if (name.includes("ruby")) return "rb";
    if (name.includes("c#")) return "cs";
    if (name.includes("go")) return "go";
    if (name.includes("rust")) return "rs";
    if (name.includes("php")) return "php";

    return "txt";
  };

  // Run code
  const runCode = async () => {
    if (!selectedLanguage) {
      setStatus("Please select a language");
      return;
    }

    if (!activeFile || !activeFile.content.trim()) {
      setStatus("No code to run");
      return;
    }

    setIsRunning(true);
    setOutput("Running code...");
    setStatus("Submitting code for execution...");
    setActiveTab("output");

    try {
      // Prepare the submission with base64 encoding
      const data = {
        language_id: selectedLanguage.id,
        source_code: btoa(activeFile.content),
        stdin: input ? btoa(input) : "",
        base64_encoded: true,
      };

      console.log(activeFile.content);

      // Submit the code
      const response = await axios.post(
        `${api_url}/submissions?base64_encoded=true&fields=*`,
        data,
        { headers }
      );

      if (!response.data.token) {
        throw new Error("No token received");
      }

      const token = response.data.token;

      // Poll for results
      await pollSubmission(token);
    } catch (error) {
      console.error("Execution error:", error);
      setOutput(`Error: ${error.message || "Failed to execute code"}`);
      setStatus("Execution failed");
      setIsRunning(false);
    }
  };

  // Poll submission status
  const pollSubmission = async (token) => {
    try {
      const response = await axios.get(
        `${api_url}/submissions/${token}?base64_encoded=true&fields=*`,
        { headers }
      );

      const submission = response.data;
      const statusId = submission.status?.id;

      // If still processing, poll again
      if (statusId === 1 || statusId === 2) {
        setTimeout(() => pollSubmission(token), 1500);
        return;
      }

      processResult(submission);
    } catch (error) {
      console.error("Error polling submission:", error);
      setOutput(`Error polling submission: ${error.message}`);
      setStatus("Execution failed");
      setIsRunning(false);
    }
  };

  // Process execution result
  const processResult = (submission) => {
    const status = submission.status || {};
    const statusId = status.id;
    const statusDescription = status.description || "Unknown status";

    // Decode outputs - handle empty values
    let stdout = "";
    let stderr = "";
    let compileOutput = "";
    let message = "";

    try {
      if (submission.stdout) {
        stdout = atob(submission.stdout);
      }
    } catch (e) {
      stdout = submission.stdout || "";
    }

    try {
      if (submission.stderr) {
        stderr = atob(submission.stderr);
      }
    } catch (e) {
      stderr = submission.stderr || "";
    }

    try {
      if (submission.compile_output) {
        compileOutput = atob(submission.compile_output);
      }
    } catch (e) {
      compileOutput = submission.compile_output || "";
    }

    try {
      if (submission.message) {
        message = atob(submission.message);
      }
    } catch (e) {
      message = submission.message || "";
    }

    let result = `Status: ${statusDescription}\n\n`;

    if (compileOutput) {
      result += `Compilation output:\n${compileOutput}\n\n`;
    }

    if (stdout) {
      result += `Standard output:\n${stdout}\n\n`;
    }

    if (stderr) {
      result += `Standard error:\n${stderr}\n\n`;
    }

    if (message) {
      result += `Message: ${message}\n\n`;
    }

    // Add time and memory info
    if (submission.time) {
      result += `Execution time: ${submission.time} seconds\n`;
    }

    if (submission.memory) {
      result += `Memory used: ${submission.memory} KB\n`;
    }

    setOutput(result);

    if (statusId === 3) {
      setStatus("Execution completed successfully");
    } else {
      setStatus(`Execution completed with status: ${statusDescription}`);
    }

    setIsRunning(false);
  };

  // Effect to focus the input when editing starts
  useEffect(() => {
    if (editingFile && fileNameInputRef.current) {
      fileNameInputRef.current.focus();

      // Select the name without the extension
      const fileName = fileNameInputRef.current.value;
      const dotIndex = fileName.lastIndexOf(".");

      if (dotIndex > 0) {
        // Set selection range to select everything before the dot
        setTimeout(() => {
          fileNameInputRef.current.setSelectionRange(0, dotIndex);
        }, 0);
      } else {
        // If no extension, select all text
        fileNameInputRef.current.select();
      }
    }
  }, [editingFile]);

  // Save code to server
  const saveToServer = async () => {
    if (!activeFile || !activeFile.content.trim()) {
      setStatus("No code to save to server");
      return;
    }

    try {
      setStatus("Saving code to server...");
      setServerSaveStatus("Saving...");
      setShowServerSaveMessage(true);

      console.log(activeFile.name);
      console.log(activeFile.content);
      console.log(topic);
      const response = await axios.post(`${backendURL}/save_code`, {
        filename: activeFile.name,
        code: activeFile.content,
        topic: topic,
      });

      if (response.status === 200) {
        setServerSaveStatus("Code saved successfully to server.");
        setStatus("Code saved successfully to server.");
      } else {
        setServerSaveStatus("Failed to save code to server.");
        setStatus("Failed to save code to server.");
      }
    } catch (error) {
      console.error("Error saving code to server:", error);
      setServerSaveStatus(
        `Error: ${error.message || "Failed to save to server"}`
      );
      setStatus("Error saving code to server");
    }

    // Auto-hide the message after 5 seconds
    setTimeout(() => {
      setShowServerSaveMessage(false);
    }, 5000);
  };

  // Add a function to force cpp language for files without extensions
  const ensureCorrectSyntaxHighlighting = () => {
    // Check if the active file has no extension and update its language
    if (
      activeFile &&
      !activeFile.name.includes(".") &&
      activeFile.language !== "cpp"
    ) {
      setFiles((prevFiles) =>
        prevFiles.map((file) =>
          file.id === activeFile.id ? { ...file, language: "cpp" } : file
        )
      );
    }
  };

  // Call this function whenever activeFileId changes
  useEffect(() => {
    // After the activeFile is updated, ensure it has the correct syntax highlighting
    ensureCorrectSyntaxHighlighting();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFileId, activeFile?.name]);

  return (
    <div className="flex justify-center bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white">
      <div className="w-full max-w-6xl flex flex-col space-y-4">
        {/* Control Panel */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Language Selection */}
            <div className="flex items-center">
              <label className="hidden sm:block text-gray-700 dark:text-gray-300 mr-2">
                Language:
              </label>
              <select
                value={selectedLanguage?.id || ""}
                onChange={handleLanguageChange}
                className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {judgeLanguages.map((lang) => (
                  <option key={lang.id} value={lang.id}>
                    {lang.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Theme Selection */}
            <div className="flex items-center">
              <label className="hidden sm:block text-gray-700 dark:text-gray-300 mr-2">
                Theme:
              </label>
              <select
                value={selectedTheme}
                onChange={handleThemeChange}
                className="bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md px-3 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {editorThemes.map((theme) => (
                  <option key={theme.value} value={theme.value}>
                    {theme.label}
                  </option>
                ))}
              </select>
            </div>

            {/* File Operations */}
            <div className="flex items-center gap-2 ml-auto">
              <button
                onClick={newFile}
                className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center"
                title="New File (Ctrl+N)"
              >
                <DocumentPlusIcon className="h-5 w-5" />
              </button>
              <button
                onClick={openFile}
                className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center"
                title="Open File (Ctrl+O)"
              >
                <FolderOpenIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => saveFile(activeFileId)}
                className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center"
                title="Save File (Ctrl+S)"
              >
                <DocumentArrowDownIcon className="h-5 w-5" />
              </button>
              <button
                onClick={formatCode}
                className="bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 p-2 rounded hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors flex items-center"
                title="Format Code (Shift+Alt+F)"
              >
                <CodeBracketIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => saveToServer()}
                className="bg-purple-500 text-white p-2 rounded hover:bg-purple-600 transition-colors flex items-center"
                title="Save to Server"
              >
                <CloudArrowUpIcon className="h-5 w-5" />
              </button>
              <button
                onClick={runCode}
                className="bg-green-500 text-white p-2 rounded hover:bg-green-600 transition-colors flex items-center"
                title="Run Code (F5)"
                disabled={isRunning}
              >
                <PlayIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Server save message */}
          {showServerSaveMessage && (
            <div className="mt-3 p-2 rounded-md bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 text-sm">
              {serverSaveStatus}
            </div>
          )}
        </div>

        {/* File Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-t-lg shadow-lg overflow-x-auto">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            {files.map((file) => (
              <div
                key={file.id}
                className={`flex items-center px-4 py-2 border-r border-gray-200 dark:border-gray-700 ${
                  activeFileId === file.id
                    ? "bg-blue-50 dark:bg-gray-700 text-blue-600 dark:text-blue-300"
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                }`}
              >
                {editingFile === file.id ? (
                  <input
                    ref={fileNameInputRef}
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    onBlur={() => {
                      if (editingName.trim()) {
                        renameFile(file.id, editingName);
                      }
                      setEditingFile(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        if (editingName.trim()) {
                          renameFile(file.id, editingName);
                        }
                        setEditingFile(null);
                      } else if (e.key === "Escape") {
                        setEditingFile(null);
                      }
                    }}
                    className="bg-transparent border-b border-blue-500 outline-none px-1 w-20 min-w-0"
                    autoFocus
                  />
                ) : (
                  <button
                    className="font-medium mr-2 focus:outline-none"
                    onClick={() => setActiveFileId(file.id)}
                    onDoubleClick={() => {
                      setEditingFile(file.id);
                      setEditingName(file.name);
                      // Focus will be set by the ref on the next render
                    }}
                  >
                    {file.name}
                  </button>
                )}
                <button
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 focus:outline-none"
                  onClick={() => closeFile(file.id)}
                  title="Close Tab (Ctrl+W)"
                >
                  <XMarkIcon className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Monaco Code Editor */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-b-lg shadow-lg overflow-hidden h-[400px] -mt-4">
          <Editor
            height="75vh"
            width="100%"
            language={
              activeFile
                ? activeFile.name.includes(".")
                  ? activeFile.language
                  : "cpp"
                : "cpp"
            }
            theme={selectedTheme}
            value={activeFile ? activeFile.content : ""}
            onChange={handleCodeChange}
            onMount={handleEditorDidMount}
            options={{
              readOnly: false,
              minimap: { enabled: false },
            }}
          />
        </div>

        {/* Tabs for Output and Input */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="flex border-b border-gray-200 dark:border-gray-700">
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === "output"
                  ? "text-blue-500 border-b-2 border-blue-500"
                  : "text-gray-700 dark:text-gray-300"
              }`}
              onClick={() => setActiveTab("output")}
            >
              Output
            </button>
            <button
              className={`px-4 py-2 font-medium ${
                activeTab === "input"
                  ? "text-blue-500 border-b-2 border-blue-500"
                  : "text-gray-700 dark:text-gray-300"
              }`}
              onClick={() => setActiveTab("input")}
            >
              Input
            </button>
          </div>

          <div className="p-4">
            {activeTab === "output" ? (
              <textarea
                value={output}
                readOnly
                className="w-full h-32 font-mono text-sm resize-none border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              ></textarea>
            ) : (
              <textarea
                value={input}
                onChange={handleInputChange}
                placeholder="Enter input for your program here..."
                className="w-full h-32 font-mono text-sm resize-none border rounded p-2 focus:outline-none focus:ring-2 focus:ring-blue-500 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200"
              ></textarea>
            )}
          </div>
        </div>

        {/* Status Bar */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-2 text-gray-700 dark:text-gray-300 text-sm">
          {status}
        </div>
      </div>
    </div>
  );
};

export default IDETab;
